import React, { useEffect, useState } from "react";
import { Typography, CircularProgress, Alert, Button, Box } from "@mui/material";
import PageTitle from "../../components/PageTitle/PageTitle";
import "../OrderConfirmation/OrderConfirmation.css";
import { ContactMailOutlined, TaskAltOutlined, WarningAmberOutlined } from "@mui/icons-material";
import { useAuth } from "../../context/authContext";
import OrderDetails from "../../components/OrderDetails/OrderDetails";
import { ALERT_MESSAGES, BUTTON, TITLES, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { alpha } from "@mui/system";
import { useSite } from "../../context/siteContext";
import { getProductsById } from "../../services/cartService";

const OrderFailure = () => {

    const location = useLocation();
    const navigate = useNavigate();
    const orderData = location.state?.orderData;
    const {
        shipping_address,
        billing_address,
        additional_details,
        payment_details,
        order_details,
        selectedSite: selSiteId,
        loggedin_user,
        loggedin_user_email,
        loggedin_user_name,
        loggedin_user_phone,
    } = orderData;
    const { selectedSite } = useSite();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [order, setOrder] = useState(null);
    const [productDetailsMap, setProductDetailsMap] = useState({});
    const { t } = useTranslation();



    useEffect(() => {
        const fetchProductsAndGenerateOrder = async () => {
            if (!order_details || !selectedSite) return;

            setLoading(true);
            try {
                // 1️⃣ Fetch product details
                const products = order_details.products.map((p) => p.product_id);
                const site_number = selectedSite;
                const resp = await getProductsById(products,site_number);

                const productMap = resp?.data?.reduce((acc, product) => {
                    acc[product.product_id] = product;
                    return acc;
                }, {});
                console.log(productMap)
                setProductDetailsMap(productMap || {});

                // 2️⃣ Once products are fetched, create the order entry
                if (!orderData) return;

                const orderEntry = {
                    order: {
                        user_display_name: loggedin_user_name,
                        order_id: "Failed",
                        order_number: "NA",
                        user_id: loggedin_user,
                        site_id: selectedSite,
                        order_taxes: order_details.taxes,
                        order_discounts: order_details.discounts,
                        order_shipping_cost: order_details.shipping,
                        order_total: order_details.total,
                        order_status: order_details.status || "FAILED",
                        order_currency: order_details.currency || "USD",
                        order_key: order_details.order_key || "",
                        order_payment_mode: payment_details.payment_mode,
                        order_created_date: order_details.created_date || new Date().toISOString(),
                        order_update_date: order_details.updated_date || new Date().toISOString(),
                        site_number: order_details.site_number || "",
                    },
                    products:
                        order_details.products?.map((p) => ({
                            id: p.op_id || p.product_id,
                            order_id: "Failed",
                            product_id: p.product_id,
                            product_quantity: p.ProductQty,
                            product_price: p.ProductPrice,
                            product_total: p.total,
                            productsData: productMap?.[p.product_id] || null,
                        })) || [],
                    details: {
                        id: order_details.details_id || "",
                        order_id: "Failed",
                        shipping_address: shipping_address.name,
                        shipping_name: shipping_address.name,
                        shipping_street: shipping_address.street,
                        shipping_city: shipping_address.city,
                        shipping_zip: shipping_address.zip,
                        shipping_country: shipping_address.country,
                        shipping_state: shipping_address.state,
                        billing_address: billing_address.name,
                        billing_name: billing_address.name,
                        billing_street: billing_address.street,
                        billing_city: billing_address.city,
                        billing_zip: billing_address.zip,
                        billing_country: billing_address.country,
                        billing_state: billing_address.state,
                        delivery_terms: additional_details.delivery_terms,
                        ship_complete: additional_details.ship_complete,
                        mode_of_delivery: additional_details.mode_of_delivery,
                        freight_number: additional_details.freight_number || "",
                        order_notes: additional_details.order_notes || "",
                        po_number: payment_details.payment_details.po_number,
                        po_filename: payment_details.payment_details.po_filename || "",
                    },
                };

                console.log("Transformed orderEntry:", orderEntry);
                setOrder(orderEntry);
            } catch (err) {
                console.error("Error fetching product details:", err);
                setError("Failed to load product details");
            } finally {
                setLoading(false);
            }
        };

        fetchProductsAndGenerateOrder();
    }, [order_details, selectedSite, orderData]);


    if (!orderData) {
        navigate("/checkout");
        return null;
    }

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "80vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 3 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box
            sx={{
                width: "80vw",
                display: "flex",
                justifyContent: "flex-start",
                flexDirection: "column",
                minHeight: "800px",
                padding: "40px",
            }}
        >
            <PageTitle title={"Quote Failure"} />
            <Box sx={{ background: (theme) => alpha(theme.palette.error.light, 0.1), borderRadius: '10px', display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', py: 2, px: 2.5, columnGap: 2 }}>
                <WarningAmberOutlined sx={{ color: "error.main", fontSize: '50px' }} />
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }} color="error.main">
                        {t('ORDERS.FAILED_TO_PLACE_ORDER')}!
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        <b>{t("UI_TEXT.ORDERS")}</b> NA
                    </Typography>
                </Box>
            </Box>

            <Typography variant="body1" sx={{ mt: 1, px: 1 }}>
                Please try to create the quote again or contact ccr for further assistance
            </Typography>

            {order &&
                <Box sx={{ width: '100%' }}>
                    <OrderDetails orderData={order} />
                </Box>
            }

            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', columnGap: 1 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate("/cart")}
                >
                    Retry Quote
                </Button>
                {/* <Button startIcon={<ContactMailOutlined />} variant="outlined" className="button-primary"
                    onClick={() => navigate("/support")}>
                   {t('LABELS.CONTACT_CCR')}
                </Button> */}
            </Box>
        </Box>
    );
};

export default OrderFailure;
